import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import Navbar from "@/components/Navbar";
import StatusBadge from "@/components/StatusBadge";
import RouteMap from "@/components/RouteMap";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { ArrowLeft, Loader2, Navigation, Route, MapPin, RefreshCw, CheckCircle2 } from "lucide-react";

export default function DriverRoute() {
  const navigate = useNavigate();
  const [route, setRoute] = useState(null);
  const load = useCallback(() => api.get("/driver/route").then((r) => setRoute(r.data)).catch(() => setRoute(false)), []);
  useEffect(() => { load(); }, [load]);

  const nav = (s) => `https://www.google.com/maps/dir/?api=1&destination=${s.lat},${s.lng}`;

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main className="mx-auto max-w-3xl px-4 py-8 sm:px-6">
        <div className="flex items-center justify-between">
          <Button variant="ghost" size="sm" onClick={() => navigate("/driver")} data-testid="route-back-button" className="gap-1.5"><ArrowLeft className="h-4 w-4" /> Jobs</Button>
          <Button variant="outline" size="sm" onClick={load} data-testid="route-refresh-button" className="gap-1.5"><RefreshCw className="h-3.5 w-3.5" /> Refresh</Button>
        </div>
        <h1 className="mt-2 flex items-center gap-2 font-heading text-3xl font-extrabold tracking-tight text-kilted-green"><Route className="h-7 w-7 text-kilted-gold" /> Optimized route</h1>

        {route === null ? (
          <div className="mt-16 flex justify-center"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>
        ) : route === false ? (
          <p className="mt-10 text-center text-muted-foreground">Couldn't load your route.</p>
        ) : route.stops.length === 0 ? (
          <div className="mt-10 text-center" data-testid="route-empty">
            {route.total_today > 0 ? (
              <p className="flex items-center justify-center gap-2 font-semibold text-emerald-700"><CheckCircle2 className="h-5 w-5" /> All {route.total_today} stops done today — great run!</p>
            ) : (
              <p className="text-muted-foreground">No active drops to route — new jobs will appear here.</p>
            )}
          </div>
        ) : (
          <>
            <p className="mt-1 text-sm text-muted-foreground" data-testid="route-summary">{route.stops.length} stops · ~{route.total_km} km · shortest-path order from {route.start.label}</p>
            {route.total_today > 0 && (
              <div className="mt-4 rounded-lg border border-kilted-green/20 bg-white p-4" data-testid="route-progress-card">
                <div className="mb-2 flex items-center justify-between text-sm">
                  <span className="font-semibold text-kilted-green" data-testid="route-progress-label">{route.done_today} of {route.total_today} stops done today</span>
                  <span className="text-muted-foreground">{Math.round((route.done_today / route.total_today) * 100)}%</span>
                </div>
                <Progress value={(route.done_today / route.total_today) * 100} className="h-2.5 [&>div]:bg-kilted-gold" data-testid="route-progress-bar" />
              </div>
            )}
            <div className="mt-4"><RouteMap start={route.start} stops={route.stops} /></div>
            <div className="mt-6 space-y-3" data-testid="route-stops">
              <div className="flex items-center gap-3 rounded-lg border border-kilted-green/30 bg-kilted-green/5 p-3">
                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-kilted-green font-bold text-white">S</div>
                <div><p className="text-sm font-semibold">{route.start.label}</p><p className="text-xs text-muted-foreground">Start</p></div>
              </div>
              {route.stops.map((s, i) => (
                <div key={s.order_id} className="flex items-center gap-3 rounded-lg border border-border bg-white p-3" data-testid={`route-stop-${i}`}>
                  <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-kilted-gold font-bold text-kilted-green">{i + 1}</div>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-semibold">{s.business || s.line1}</p>
                    <p className="truncate text-xs text-muted-foreground"><MapPin className="mr-0.5 inline h-3 w-3" />{[s.line1, s.city, s.postcode].filter(Boolean).join(", ")} · +{s.leg_km} km</p>
                    {s.reference && <p className="truncate text-xs text-muted-foreground">{s.reference}</p>}
                  </div>
                  <div className="flex shrink-0 flex-col items-end gap-1.5">
                    <StatusBadge status={s.status} />
                    <a href={nav(s)} target="_blank" rel="noreferrer" data-testid={`route-navigate-${i}`}
                      className="inline-flex items-center gap-1 rounded-full border border-kilted-green px-2.5 py-1 text-xs font-medium text-kilted-green hover:bg-kilted-green/10">
                      <Navigation className="h-3 w-3" /> Navigate
                    </a>
                  </div>
                </div>
              ))}
            </div>
            {route.unrouteable.length > 0 && (
              <Card className="mt-4 border-amber-200"><CardContent className="pt-4 text-xs text-amber-700" data-testid="route-unrouteable">
                {route.unrouteable.length} job(s) have no map coordinates and aren't in the route: {route.unrouteable.map((u) => u.tracking_number).join(", ")}
              </CardContent></Card>
            )}
          </>
        )}
      </main>
    </div>
  );
}
